import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UserForm } from "./UserForm";
import { useToast } from "@/hooks/use-toast";
import { Grid2X2 } from "lucide-react";
import { UsersTable } from "./UsersTable";
import { useUsers, User } from "@/hooks/useUsers";
import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

export interface UserFormData {
  name: string;
  email: string;
  type: string;
  companyId?: string;
  password?: string;
}

export const UserSettings = () => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const { users, isLoading, addUser, updateUser, deleteUser, refreshUsers } = useUsers();
  const { toast } = useToast();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      console.log('UserSettings: Refreshing users data...');
      refreshUsers();
    }
  }, [currentUser, refreshUsers]);

  const handleSubmit = async (data: UserFormData) => {
    try {
      if (!data.name || !data.email || !data.type) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Nome, email e tipo são campos obrigatórios.",
        });
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Email inválido.",
        });
        return;
      }

      if (currentUser?.type === 'admin') {
        if (data.type === 'super_admin') {
          toast({
            variant: "destructive",
            title: "Erro",
            description: "Administradores não podem criar super administradores.",
          });
          return;
        }
        data.companyId = currentUser.companyId;
      }

      console.log('Submitting user data:', {
        ...data,
        editingId
      });
      
      if (editingId) {
        await updateUser(editingId, data);
        setEditingId(null);
      } else {
        await addUser(data);
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      
      if (error.message?.includes('duplicate key value') || 
          error.message?.includes('users_email_key')) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Este e-mail já está cadastrado no sistema.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Erro",
          description: error.message || "Falha ao salvar usuário.",
        });
      }
    }
  };

  const handleEdit = (user: User) => {
    if (currentUser?.type === 'admin' && user.type === 'super_admin') {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Você não tem permissão para editar super administradores.",
      });
      return;
    }
    
    console.log('Editing user:', user);
    setEditingId(user.id);
  };

  const handleDelete = async (userId: string) => {
    try {
      await deleteUser(userId);
    } catch (error) {
      console.error('Error in handleDelete:', error);
    }
  };

  const handleClear = () => {
    setEditingId(null);
  };

  console.log('UserSettings render - isLoading:', isLoading, 'users:', users);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center space-x-2">
            <Grid2X2 className="h-6 w-6" />
            <CardTitle>
              {currentUser?.type === 'super_admin'
                ? "Gerenciar Todos os Usuários"
                : "Gerenciar Usuários da Empresa"}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <UserForm
            onSubmit={handleSubmit}
            initialData={users.find((user) => user.id === editingId)}
            onClear={handleClear}
            isEditing={editingId !== null}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Usuários Cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          <UsersTable 
            users={users} 
            onEdit={handleEdit} 
            onDelete={handleDelete} 
          />
        </CardContent>
      </Card>
    </div>
  );
};
